/**
 * Flowlytics Pixel SDK - Discovery Integration
 *
 * Runs once at SDK init to build a static snapshot of the page's analytics stack:
 *   - Which tracking tools are installed (GA4, Facebook, TikTok, LinkedIn, Pinterest)
 *   - GTM container IDs present on the page
 *   - Analytics platform IDs extracted from scripts/dataLayer (GA4 G-XXX, FB Pixel ID, etc.)
 *   - Events already in window.dataLayer BEFORE the pixel loaded (pre-existing events)
 *
 * All data is sent to POST /api/pixel/discovery (no auth, api_key in body).
 * The backend stores it in pixel_event_catalog so the portal can suggest mappings
 * without waiting for real user interactions.
 *
 * This runs asynchronously (setTimeout 0) to avoid delaying page_view.
 */

const GTM_INTERNAL_EVENTS = new Set([
  "gtm.js",
  "gtm.dom",
  "gtm.load",
  "gtm.init",
  "gtm.init_consent",
  "gtm.click",
  "gtm.linkClick",
  "gtm.formSubmit",
  "gtm.historyChange",
  "gtm.scrollDepth",
  "gtm.timer",
  "gtm.video",
  "consent_default",
  "consent_init",
  "optimize.domChange",
  "optimize.callback",
  "page_view",
  "pageview",
]);

class DiscoveryIntegration {
  constructor(config) {
    this.config = config;
    this._sent = false;
  }

  init() {
    // GTM injects GA4/fbq.js dynamically after window.load via script tags.
    // We poll every 500ms (max 10s) until at least one tool global appears,
    // then fire. This avoids a fixed long delay while still catching late loaders.
    // Falls back unconditionally at 10s even if no tool is found.
    const startPoll = () => this._pollForTools();

    if (document.readyState === "complete") {
      // Page already loaded — start polling immediately
      startPoll();
    } else {
      window.addEventListener("load", startPoll, { once: true });
    }
  }

  _pollForTools(elapsed = 0) {
    if (this._sent) return;

    const POLL_INTERVAL = 500;
    const MAX_WAIT = 10000;

    const toolsVisible =
      typeof window.gtag === "function" ||
      typeof window.fbq === "function" ||
      typeof window.ttq === "object" ||
      typeof window.lintrk === "function";

    if (toolsVisible || elapsed >= MAX_WAIT) {
      this._run();
    } else {
      setTimeout(
        () => this._pollForTools(elapsed + POLL_INTERVAL),
        POLL_INTERVAL,
      );
    }
  }

  _run() {
    if (this._sent) return;
    this._sent = true;

    try {
      const snapshot = this._takeSnapshot();
      if (this.config.debug) {
        console.log("[Flowlytics Discovery] Snapshot:", snapshot);
      }
      this._send(snapshot);
    } catch (err) {
      if (this.config.debug) {
        console.warn("[Flowlytics Discovery] Snapshot failed:", err.message);
      }
    }
  }

  _takeSnapshot() {
    const tools = [];
    const gtm_containers = [];
    const analytics_ids = {};
    const datalayer_events = new Set();

    // ── GTM container IDs ────────────────────────────────────────────────────
    // From google_tag_manager global (most reliable — GTM writes this on load)
    if (window.google_tag_manager) {
      Object.keys(window.google_tag_manager)
        .filter((k) => /^GTM-/i.test(k))
        .forEach((id) => gtm_containers.push(id));
    }
    // From <script src> tags (catches GTM loaded before google_tag_manager was written)
    document
      .querySelectorAll('script[src*="googletagmanager.com/gtm.js"]')
      .forEach((s) => {
        try {
          const m = new URL(s.src).searchParams.get("id");
          if (m && !gtm_containers.includes(m)) gtm_containers.push(m);
        } catch (_) {}
      });

    // ── Pre-existing dataLayer events ────────────────────────────────────────
    // Scan items pushed to dataLayer BEFORE the pixel SDK loaded.
    // These would never trigger our dataLayer.push() proxy.
    (window.dataLayer || []).forEach((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return;
      const name = item.event;
      if (!name || typeof name !== "string") return;
      if (GTM_INTERNAL_EVENTS.has(name) || name.startsWith("gtm.")) return;
      datalayer_events.add(name);

      // Extract GA4 Measurement ID from gtag config pushes if present
      // Pattern: {event: 'gtm.js'} or array ['config', 'G-XXXXX'] → handled below
    });

    // Scan array-style dataLayer items (gtag pushes)
    (window.dataLayer || []).forEach((item) => {
      if (!Array.isArray(item)) return;
      if (
        item[0] === "config" &&
        typeof item[1] === "string" &&
        item[1].startsWith("G-")
      ) {
        analytics_ids.ga4 = item[1];
      }
    });

    // ── Installed analytics tools ────────────────────────────────────────────
    // Detection is multi-layered: globals (most reliable after window.load),
    // script tags, and inline script text (catches IDs injected via GTM).

    // GA4 / gtag — check global + script tags + inline scripts
    if (typeof window.gtag === "function" || typeof window.ga === "function") {
      tools.push("ga4");
    }
    // GA4 ID from gtag.js src param
    document
      .querySelectorAll('script[src*="googletagmanager.com/gtag/js"]')
      .forEach((s) => {
        try {
          const id = new URL(s.src).searchParams.get("id");
          if (id && id.startsWith("G-")) {
            if (!tools.includes("ga4")) tools.push("ga4");
            analytics_ids.ga4 = id;
          }
        } catch (_) {}
      });
    // GA4 ID from inline scripts: gtag('config', 'G-XXXXXX')
    if (!analytics_ids.ga4) {
      try {
        document.querySelectorAll("script:not([src])").forEach((s) => {
          const m = s.textContent.match(
            /gtag\s*\(\s*['"]config['"]\s*,\s*['"](\bG-[A-Z0-9]+\b)['"]/,
          );
          if (m && !analytics_ids.ga4) {
            analytics_ids.ga4 = m[1];
            if (!tools.includes("ga4")) tools.push("ga4");
          }
        });
      } catch (_) {}
    }

    // Facebook Pixel — check global + inline scripts
    if (typeof window.fbq === "function") {
      tools.push("facebook_pixel");
      try {
        const pixels = window.fbq?.getState?.()?.pixels || [];
        if (pixels.length) analytics_ids.fb_pixel = pixels[0].id;
      } catch (_) {}
    }
    // FB Pixel ID from inline scripts: fbq('init', 'XXXXXXXX')
    if (!analytics_ids.fb_pixel) {
      try {
        document.querySelectorAll("script:not([src])").forEach((s) => {
          const m = s.textContent.match(
            /fbq\s*\(\s*['"]init['"]\s*,\s*['"](\d{10,})['"]/,
          );
          if (m && !analytics_ids.fb_pixel) {
            analytics_ids.fb_pixel = m[1];
            if (!tools.includes("facebook_pixel")) tools.push("facebook_pixel");
          }
        });
      } catch (_) {}
    }

    // TikTok Pixel
    if (typeof window.ttq === "object" && window.ttq) {
      tools.push("tiktok_pixel");
      try {
        const ids = Object.keys(window.ttq._i || {});
        if (ids.length) analytics_ids.tiktok_pixel = ids[0];
      } catch (_) {}
    }

    // LinkedIn Insight Tag
    if (typeof window.lintrk === "function") {
      tools.push("linkedin_pixel");
      try {
        document.querySelectorAll("script:not([src])").forEach((s) => {
          const m = s.textContent.match(
            /lintrk\s*\(\s*['"]init['"]\s*,\s*\{[^}]*partner_id['"]\s*:\s*['"]?(\d+)['"]?/,
          );
          if (m && !analytics_ids.linkedin_partner_id)
            analytics_ids.linkedin_partner_id = m[1];
        });
      } catch (_) {}
    }

    // Pinterest Tag
    if (typeof window.pintrk === "function") {
      tools.push("pinterest_pixel");
    }

    // Segment
    if (typeof window.analytics === "object" && window.analytics?.__loaded) {
      tools.push("segment");
    }

    // Hotjar
    if (typeof window.hj === "function") {
      tools.push("hotjar");
    }

    return {
      gtm_containers,
      tools: [...new Set(tools)],
      analytics_ids,
      datalayer_events: [...datalayer_events],
      page_url: window.location.href,
      collected_at: new Date().toISOString(),
    };
  }

  _send(snapshot) {
    const endpoint = this._resolveEndpoint();
    if (!endpoint) return;

    const payload = {
      api_key: this.config.apiKey,
      snapshot,
    };

    // keepalive: true so the request survives page unload
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Best-effort — silently ignore failures
    });
  }

  _resolveEndpoint() {
    // Derive base URL from the SDK script src
    try {
      const scripts = document.querySelectorAll('script[src*="flowlytics"]');
      for (const s of scripts) {
        if (s.src.includes("/sdk/")) {
          return new URL(s.src).origin + "/api/pixel/discovery";
        }
      }
    } catch (_) {}

    // Fallback: derive from pixel events endpoint
    try {
      const eventsEndpoint = this.config.endpoint || "";
      if (eventsEndpoint.startsWith("http")) {
        return eventsEndpoint.replace(
          /\/api\/pixel\/events.*$/,
          "/api/pixel/discovery",
        );
      }
    } catch (_) {}

    return null;
  }
}

export default DiscoveryIntegration;
